import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { GridColDef } from "@material-ui/data-grid";

import BaseDataGrid from "../../../../../components/modules/BaseDataGrid";
import ConfirmationModal from "../../../../../components/modules/ConfirmationModal";
import LineItemsReportDashboardToolbar from "./LineItemsReportDashboardToolbar";
import Loader from "../../../../../components/elements/Loader";
import Switch from "../../../../../components/elements/Switch";
import api from "../../../../../api";
import classes from "../../CampaignsDashboard.module.scss";
import { CampaignLinteItemType, CampaignType, ReportTimeFrames, ReportType } from "../../../../../types/campaignTypes";
import { floatingNumbersFields, metricsColumns } from "../../constants";
import { transformRowValue } from "../../../../../helpers/campainRowFormatHelpers";
import { truncateText } from "../../../../../helpers/truncateText";

const fetchLineItems = async (brandgenCampaignId: string, selectedTimeFrame: string) => {
  try {
    const { payload } = await api.getLineItemReports(brandgenCampaignId, selectedTimeFrame);
    return payload;
  } catch (error) {
    const err = error as AxiosError;
    throw new Error(err.message);
  }
};

type CampaignLineItemsReportDashboardProps = {
  selectedTimeFrame: ReportTimeFrames;
  campaign: CampaignType;
};

type LineItemToUpdate = {
  id: number;
  active: boolean;
};

const CampaignLineItemsReportDashboard: FC<CampaignLineItemsReportDashboardProps> = ({
  selectedTimeFrame,
  campaign,
}) => {
  const [lineItems, setLineItems] = useState<CampaignLinteItemType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sourceHeaderName, setSourceHeaderName] = useState<string>("Source");

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [lineItemToUpdate, setLineItemToUpdate] = useState<LineItemToUpdate | null>(null);

  const handleOpenConfirmationModal = () => setIsConfirmationModalOpen(true);
  const handleCloseConfirmationModal = () => setIsConfirmationModalOpen(false);

  useEffect(() => {
    const fetchAndSetLineItemReports = async () => {
      try {
        setIsLoading(true);
        const fetchedLineItemReports = await fetchLineItems(campaign.brandgenCampaignId, selectedTimeFrame);
        setLineItems(fetchedLineItemReports);
        setIsLoading(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("fetchAndSetLineItemReports error: ", error);
        setIsLoading(false);
      }
    };

    fetchAndSetLineItemReports();
  }, [campaign.brandgenCampaignId, selectedTimeFrame, campaign.active]);

  useEffect(() => {
    if (lineItems && lineItems.length > 0) {
      const headerName = !lineItems[0].hubspotCompanyId ? "File Upload" : "Source";
      setSourceHeaderName(headerName);
    } else {
      setSourceHeaderName("Source");
    }
  }, [lineItems]);

  const updateLineItemActiveStatus = async () => {
    try {
      setIsLoading(true);
      if (lineItemToUpdate) {
        await api.updateLineItemStatus(lineItemToUpdate.id, !lineItemToUpdate.active);
        const fetchedLineItemReports = await fetchLineItems(campaign.brandgenCampaignId, selectedTimeFrame);
        setLineItems(fetchedLineItemReports);
        setIsLoading(false);
        setLineItemToUpdate(null);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("updateLineItemActiveStatus error: ", error);
      setIsLoading(false);
    }
  };

  const handleLineItemActiveStatusChange = (id: number, active: boolean) => () => {
    handleOpenConfirmationModal();
    setLineItemToUpdate({
      id,
      active,
    });
  };

  const columns: GridColDef[] = [
    {
      field: "active",
      headerName: "Status",
      width: 150,
      // eslint-disable-next-line react/display-name
      renderCell: (params: any) =>
        params.row.active === "ACTIVE" ? (
          <>
            <Typography className={classes.activeStatus}>ACTIVE</Typography>
            <Switch
              checked={params.row.active === "ACTIVE"}
              onChange={handleLineItemActiveStatusChange(params.row.beeswaxLineItemId, params.row.active === "ACTIVE")}
            />
          </>
        ) : (
          <>
            <Typography className={classes.inactiveStatus}>INACTIVE</Typography>
            <Switch
              checked={params.row.active === "ACTIVE"}
              onChange={handleLineItemActiveStatusChange(params.row.beeswaxLineItemId, params.row.active === "ACTIVE")}
            />
          </>
        ),
    },
    {
      field: "name",
      headerName: "Name",
      width: 220,
      // eslint-disable-next-line react/display-name
      renderCell: ({ value }) => (
        <Tooltip title={value as string}>
          <Typography>{truncateText(value as string, 25)}</Typography>
        </Tooltip>
      ),
    },
    {
      field: "source",
      headerName: sourceHeaderName,
      width: 200,
    },
    {
      field: "hsCompanyId",
      headerName: "HS Company Id",
      width: 180,
    },
    ...metricsColumns,
  ];

  const rows = lineItems.map(
    ({ beeswaxLineItemId, line_item_name, lineItemReports, active, hubspotCompanyId, fromFile }, index) => {
      const lineItemReport = lineItemReports?.length ? lineItemReports[0] : null;
      const row: { [key: string]: string | number } = {};

      if (lineItemReport) {
        row.id = beeswaxLineItemId.toString() + index;
        row.hsCompanyId = hubspotCompanyId ?? "N/A";
        row.source = !hubspotCompanyId ? fromFile ?? "file" : "hs";
        row.beeswaxLineItemId = beeswaxLineItemId;
        row.name = line_item_name ?? "N/A";
        row.active = active ? "ACTIVE" : "INACTIVE";

        for (const metric of metricsColumns) {
          const metricsValue = lineItemReport[metric.field as keyof ReportType] as number;
          const isValueFloat = floatingNumbersFields.includes(metric.field);

          if (metric.type === "CURRENCY") {
            row[metric.field] = transformRowValue(metricsValue, false, isValueFloat, "CURRENCY");
          } else if (metric.type === "FLAT") {
            row[metric.field] = transformRowValue(metricsValue, true, isValueFloat, "FLAT");
          } else {
            row[metric.field] = transformRowValue(metricsValue, false, isValueFloat, "PERCENTAGE");
          }
        }

        return row;
      }

      metricsColumns.forEach((metric) => {
        row[metric.field] = "N/A";
      });
      return row;
    },
  );

  const isMaxRowsAmountExceeded = rows.length > 10;

  return (
    <div
      className={`${classes.lineItemsDashboard} ${
        isMaxRowsAmountExceeded ? classes.lineItemsDashboardFixedHeight : ""
      }`}
    >
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        handleClose={handleCloseConfirmationModal}
        confirmQuestion="Are you sure you want to update this line item's status?"
        confirmButtonLabel="Yes"
        handleConfirm={updateLineItemActiveStatus}
      />
      {isLoading ? (
        <Loader className={classes.loader} isAbsolutePosition={false} size={42} />
      ) : (
        <>
          <BaseDataGrid
            components={{
              Toolbar: LineItemsReportDashboardToolbar,
            }}
            localeText={{ toolbarColumns: "Select Metrics" }}
            autoHeight={!isMaxRowsAmountExceeded}
            columns={columns}
            rows={rows}
          />
        </>
      )}
    </div>
  );
};

export default CampaignLineItemsReportDashboard;
